import React, { useEffect, useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    ShoppingCart,
    PackageCheck,
    Truck,
    XCircle,
    IndianRupee,
    MapPin,
    Calendar,
    Loader2,
    AlertCircle,
} from "lucide-react";
import { getBookings } from "../service/userService";

const STATUS_STYLES = {
    PENDING: "bg-amber-100 text-amber-700",
    CONFIRMED: "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-indigo-100 text-indigo-700",
    COMPLETED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
};

function humanize(value) {
    if (!value) return "—";
    return value
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// Transforms a raw booking record from the API into the shape the table expects.
// NOTE: the API has no `payment` field — bookings only carry `bookingStatus`.
function mapBookingToOrder(booking) {
    return {
        id: `BK-${booking.bookingId}`,
        customer: booking.userName || "—",
        item: `${booking.machineName || "Machine"} — ${booking.purpose || "Booking"}`,
        centre: booking.facilityName || "—",
        amount: booking.totalAmount || 0,
        status: booking.bookingStatus || "PENDING",
        statusLabel: humanize(booking.bookingStatus),
        date: formatDate(booking.bookingDate),
    };
}

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function Ordermanagment() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Status");

    // ===== Fetch bookings on mount =====
    useEffect(() => {
        const fetchBookings = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getBookings();
                const rawList = Array.isArray(response) ? response : response?.data || [];

                setOrders(rawList.map(mapBookingToOrder));
            } catch (err) {
                console.error("Failed to fetch bookings:", err);
                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to load orders. Please try again."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, []);

    const statuses = useMemo(
        () => ["All Status", ...new Set(orders.map((o) => o.statusLabel))],
        [orders]
    );

    const counts = useMemo(() => {
        const totalRevenue = orders
            .filter((o) => o.status === "COMPLETED" || o.status === "CONFIRMED")
            .reduce((sum, o) => sum + o.amount, 0);
        const inProgress = orders.filter((o) => o.status === "IN_PROGRESS").length;
        const completed = orders.filter((o) => o.status === "COMPLETED").length;
        const cancelled = orders.filter((o) => o.status === "CANCELLED").length;
        return { total: orders.length, totalRevenue, inProgress, completed, cancelled };
    }, [orders]);

    const filtered = useMemo(() => {
        return orders.filter((o) => {
            const matchesQuery =
                query.trim() === "" ||
                [o.id, o.customer, o.item, o.centre].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesStatus = statusFilter === "All Status" || o.statusLabel === statusFilter;
            return matchesQuery && matchesStatus;
        });
    }, [orders, query, statusFilter]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Order management</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track fabrication and prototyping bookings across all centres
                    </p>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={ShoppingCart} label="Total bookings" value={loading ? "—" : counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard
                        icon={IndianRupee}
                        label="Revenue (confirmed)"
                        value={loading ? "—" : `₹${counts.totalRevenue.toLocaleString("en-IN")}`}
                        tone="bg-green-50 text-green-500"
                    />
                    <StatCard icon={Truck} label="In progress" value={loading ? "—" : counts.inProgress} tone="bg-blue-50 text-blue-500" />
                    <StatCard icon={XCircle} label="Cancelled" value={loading ? "—" : counts.cancelled} tone="bg-red-50 text-red-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Recent bookings</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {loading ? "Loading bookings..." : `${filtered.length} of ${orders.length} bookings`}
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search booking, customer, item..."
                                    disabled={loading}
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white disabled:opacity-50"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    disabled={loading}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300 disabled:opacity-50"
                                >
                                    {statuses.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1000px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-[30%] px-6 py-3 font-medium">Booking</th>
                                    <th className="w-[16%] px-6 py-3 font-medium">Customer</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Centre</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Amount</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Status</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 size={18} className="animate-spin" />
                                                Loading bookings...
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading && filtered.map((o, idx) => (
                                    <tr
                                        key={o.id}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-gray-900">{o.item}</p>
                                            <p className="mt-0.5 text-xs text-gray-400">{o.id}</p>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{o.customer}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div className="flex items-center gap-1.5">
                                                <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                                                {o.centre}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            ₹{o.amount.toLocaleString("en-IN")}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[o.status] || "bg-gray-100 text-gray-600"}`}>
                                                {o.statusLabel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                                                {o.date}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {!loading && !error && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No bookings match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}